/**
 * Purchase funnel data hooks — TanStack Query over the shared apiClient.
 *
 * Catalog hooks hit the public /api/v1/catalog/* endpoints (no auth);
 * funnel hooks hit /api/v1/funnel/* (clerk-authed — callers must be inside
 * AuthGuard / FunnelGuard so the apiClient token getter is wired up).
 *
 * Design: docs/technical-design-website-product.md §4.1
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import type { AddonProduct, AgreementContent, FunnelState, ProductOffering } from '@/types';

export const FUNNEL_STATE_QUERY_KEY = ['funnel', 'state'] as const;

export function useOfferings() {
  return useQuery({
    queryKey: ['catalog', 'offerings'],
    queryFn: async () => {
      const response = await apiClient.get<ProductOffering[]>('/api/v1/catalog/offerings');
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load offerings');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // mirrors the API's Cache-Control max-age=300
  });
}

export function useAddons(offeringId?: string) {
  return useQuery({
    queryKey: ['catalog', 'addons', offeringId ?? null],
    queryFn: async () => {
      const response = await apiClient.get<AddonProduct[]>(
        '/api/v1/catalog/addons',
        offeringId ? { offeringId } : undefined
      );
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load add-ons');
      }
      return response.data;
    },
    enabled: !!offeringId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnelState(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FUNNEL_STATE_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<FunnelState>('/api/v1/funnel/state');
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load funnel state');
      }
      return response.data;
    },
    enabled: options?.enabled ?? true,
    // Step routing depends on this — always revalidate on mount.
    staleTime: 0,
  });
}

export function useAgreement() {
  return useQuery({
    queryKey: ['funnel', 'agreement'],
    queryFn: async () => {
      const response = await apiClient.get<AgreementContent>('/api/v1/funnel/agreement');
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load the Service Agreement');
      }
      return response.data;
    },
  });
}
